import React from 'react';
import enhanceWithServices from 'hocs/enhanceWithServices';
import activityServiceShape from 'services/types/activityService';

/**
 * Uses the ServicesContext (containers/ServicesProvider) to subscribe
 *   to an activity and provide it as a property to an enhanced component.
 * @param {string} propName The name of property in which to provide the
 *   activity service.
 * @param {function(*): string} getActivityId A function used to assert the identity
 *   of an activity from the component's properties.  Re-runs every time the
 *   props change.  If the function returns falsy, then the wrapped component
 *   will be returned without an activity subscription.
 * @param {React.Component} PlaceholderComponent An optional component to use
 *   when an activity Id is available, but not the activity (I.e., it's loading).
 * @Param {boolean} renderComponentBeforeLoad render the wrapped component before
 *   the activity is loaded. Prop is passed thru as undefined.
*/
export default (propName, getActivityId, PlaceholderComponent, renderBeforeLoad) => (Component) => {
  class EnhancedComponent extends React.Component {
    static displayName = `EnhanceWithActivity(${Component.displayName || Component.name || 'Component'})`;

    state = {
      activity: null,
    };

    // subscribe on mount…
    componentDidMount() {
      const { activityService } = this.props;

      // Subscribe to the activity
      if (getActivityId(this.props)) {
        this.unsubscribe = activityService.subscribeToActivity(
          getActivityId(this.props), this.updateActivity
        );
      }
    }

    componentDidUpdate(prevProps) {
      const { activityService } = this.props;

      // Check to see if the activity id has changed, and if so, subscribe to
      // the new activity.
      const prevActivityId = getActivityId(prevProps);
      if (getActivityId(this.props) && (prevActivityId !== getActivityId(this.props))) {
        if (this.unsubscribe) {
          this.unsubscribe();
        }
        this.unsubscribe = activityService.subscribeToActivity(
          getActivityId(this.props), this.updateActivity
        );
      }
    }

    // unsubscribe on unmount…
    componentWillUnmount() {
      if (this.unsubscribe) {
        this.unsubscribe();
      }
    }

    updateActivity = (updatedActivity) => {
      this.setState({
        activity: updatedActivity,
      });
    };

    render() {
      const { activity } = this.state;

      if (!getActivityId(this.props)) {
        return (
          <Component
            {...this.props}
          />
        );
      }
      if (activity || renderBeforeLoad) {
        const activityProps = {
          [propName]: activity,
        };
        return (
          <Component
            {...activityProps}
            {...this.props}
          />
        );
      }
      return (PlaceholderComponent && <PlaceholderComponent />) || null;
    }
  }

  EnhancedComponent.propTypes = {
    /* An activity service, provided by the enhanceWithServices */
    activityService: activityServiceShape,
  };

  /* Grants access of the courseware services context */
  const WithServices = enhanceWithServices(EnhancedComponent);

  return WithServices;
};
